import { ToggleButton } from "primereact/togglebutton";
import { useGetDatasetsInfoBatched } from "api/api-hooks/datasets-hooks";
import { InputNumber } from "primereact/inputnumber";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import { T } from "services/lang/LanguageService";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import React, { useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import EditDatasetCellDialog from "./EditDatasetCellDialog";
import EditBasicCellDialog from "./EditBasicCellDialog";
import { getUniversalTableDatasetValues } from "../universal-dataset-table-utils";
import "./UniversalDatasetTableEditor.scss";

import { CellType, CellValuesEditorProps, ICellConfig, RowData } from "../UniversalDatasetTableTypes";

const CellValuesEditor = ({ currentConfig, setCurrentConfig }: CellValuesEditorProps) => {
  const [isDatasetValuesVisible, setIsDatasetValuesVisible] = useState<boolean>(false);
  const [isEditDatasetCellDialogVisible, setIsEditDatasetCellDialogVisible] = useState<boolean>(false);
  const [isEditBasicCellDialogVisible, setIsEditBasicCellDialogVisible] = useState<boolean>(false);
  const [selectedCell, setSelectedCell] = useState<ICellConfig>();
  const [selectedCellLocation, setSelectedCellLocation] = useState<{
    rowIndex: number;
    columnIndex: number;
  }>();

  const datasetIds = useMemo(() => getUniversalTableDatasetValues(currentConfig.rows), [currentConfig.rows]);

  const { data, isLoading, isError } = useGetDatasetsInfoBatched(datasetIds);

  const updateCellValue = (rowIndex: number, colIndex: number, value: string | number) => {
    const newConfig = {
      ...currentConfig,
      rows: currentConfig.rows.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            columns: row.columns.map((column, columnIndex) => {
              if (columnIndex === colIndex) {
                return {
                  ...column,
                  cell: {
                    ...column.cell,
                    value,
                  },
                };
              }
              return column;
            }),
          };
        }
        return row;
      }),
    };

    setCurrentConfig(newConfig);
  };

  const handleOpenDatasetCellDialog = (rowData: RowData, columnIndex: number) => {
    setSelectedCellLocation({ rowIndex: rowData.rowIndex, columnIndex });
    setIsEditDatasetCellDialogVisible(true);
    setSelectedCell(rowData.columns[columnIndex].cell);
  };

  const handleOpenBasicCellDialog = (rowData: RowData, columnIndex: number) => {
    setSelectedCellLocation({ rowIndex: rowData.rowIndex, columnIndex });
    setIsEditBasicCellDialogVisible(true);
    setSelectedCell(rowData.columns[columnIndex].cell);
  };

  const getTitleBody = (identifier?: string, numberOfLettersToDisplay?: number) => {
    if (!identifier) {
      return numberOfLettersToDisplay
        ? `${T("message.missing-dataset-identifier").slice(0, numberOfLettersToDisplay)}...`
        : T("message.missing-dataset-identifier");
    }

    if (isLoading) {
      return (
        <i>
          {identifier} ({T("word.loading")})
        </i>
      );
    }

    if (isError) {
      return `${identifier} - ${T("word.error")}`;
    }

    const allDatasets = data.flatMap(batch => batch?.data ?? []);
    const found = allDatasets.find(d => d.identifier === identifier);

    if (!found) {
      return `${identifier} - ${T("word.not-found")}`;
    }

    const { title, unit } = found;

    return (
      <>
        <span>{numberOfLettersToDisplay ? `${title?.slice(0, numberOfLettersToDisplay)}...` : title}</span>
        {!numberOfLettersToDisplay && unit && <span>|{unit}</span>}
      </>
    );
  };

  const renderDatasetValueEditor = (rowData: RowData, columnIndex: number, cellValue: string) => {
    return (
      <div className="flex flex-row gap-3 justify-content-between">
        {isDatasetValuesVisible ? (
          <span className="dataset-title-and-button">
            <CopyTextWithButton text={getTitleBody(cellValue) as string} copyableText={cellValue} />
          </span>
        ) : (
          <CopyTextWithButton text={getTitleBody(cellValue, 8) as string} copyableText={cellValue} />
        )}
        <OpenCellConfigDialogButton
          handleOpenBasicCellDialog={() => handleOpenDatasetCellDialog(rowData, columnIndex)}
        />
      </div>
    );
  };

  const renderCell = (rowData: RowData, columnIndex: number) => {
    const cell = rowData.columns[columnIndex]?.cell;
    if (!cell) return null;

    switch (cell.type) {
      case CellType.NUMBER:
        return (
          <div className="flex flex-row justify-content-between">
            <InputNumber
              className="w-min"
              value={(cell.value as number) ?? null}
              onValueChange={e => updateCellValue(rowData.rowIndex, columnIndex, e.value as number)}
            />
            <OpenCellConfigDialogButton
              handleOpenBasicCellDialog={() => handleOpenBasicCellDialog(rowData, columnIndex)}
            />
          </div>
        );

      case CellType.STRING:
      case CellType.URL:
        return (
          <div className="flex flex-row justify-content-between">
            <InputText
              className="w-min"
              value={(cell.value as string) ?? ""}
              onChange={e => updateCellValue(rowData.rowIndex, columnIndex, e.target.value)}
            />
            <OpenCellConfigDialogButton
              handleOpenBasicCellDialog={() => handleOpenBasicCellDialog(rowData, columnIndex)}
            />
          </div>
        );

      case CellType.DATASET:
        return renderDatasetValueEditor(rowData, columnIndex, cell.value as string);

      case CellType.UNIT:
        return (
          <div className="flex flex-row justify-content-between">
            <DatasetUnitSelector
              className="w-min"
              value={(cell.value as string) ?? ""}
              onChange={e => updateCellValue(rowData.rowIndex, columnIndex, e.value)}
            />
            <OpenCellConfigDialogButton
              handleOpenBasicCellDialog={() => handleOpenBasicCellDialog(rowData, columnIndex)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const updateCellConfig = (cfg: ICellConfig) => {
    if (selectedCellLocation) {
      const newConfig = {
        ...currentConfig,
        rows: currentConfig.rows.map((row, index) => {
          if (index === selectedCellLocation.rowIndex) {
            return {
              ...row,
              columns: row.columns.map((column, columnIndex) => {
                if (columnIndex === selectedCellLocation.columnIndex) {
                  return {
                    ...column,
                    cell: cfg,
                  };
                }
                return column;
              }),
            };
          }
          return row;
        }),
      };
      setCurrentConfig(newConfig);
      setIsEditDatasetCellDialogVisible(false);
    }
  };

  return (
    <div className="universal-table-cell-values-editor">
      <ToggleButton
        offLabel={T("label.display-full-dataset-titles")}
        onLabel={T("label.truncate-dataset-titles")}
        checked={isDatasetValuesVisible}
        onChange={e => setIsDatasetValuesVisible(e.value)}
        className="mb-2"
      />
      <table>
        <thead className="table-header">
          <tr>
            {currentConfig.columnsOptions.map(column => (
              <th key={column.id}>{column.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {currentConfig.rows.map((row, rowIdx) => (
            <tr key={row.uuid}>
              {row.columns.map((_, colIdx) => (
                <td key={colIdx}>
                  {renderCell(
                    {
                      isSeparator: row.isSeparator,
                      rowIndex: rowIdx,
                      columns: row.columns,
                    },
                    colIdx
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {isEditDatasetCellDialogVisible && selectedCell && (
        <EditDatasetCellDialog
          isEditDatasetCellDialogVisible={isEditDatasetCellDialogVisible}
          setIsEditDatasetCellDialogVisible={setIsEditDatasetCellDialogVisible}
          selectedDatasetCell={selectedCell}
          updateSelectedDatasetCell={updateCellConfig}
        />
      )}
      {isEditBasicCellDialogVisible && selectedCell && (
        <EditBasicCellDialog
          isEditBasicCellDialogVisible={isEditBasicCellDialogVisible}
          setIsEditBasicCellDialogVisible={setIsEditBasicCellDialogVisible}
          selectedCell={selectedCell}
          updateSelectedCell={updateCellConfig}
        />
      )}
    </div>
  );
};

export default CellValuesEditor;

const OpenCellConfigDialogButton: React.FC<{ handleOpenBasicCellDialog: () => void }> = ({
  handleOpenBasicCellDialog,
}) => {
  return (
    <i
      className="pi pi-pencil p-button-text cursor-pointer align-self-center p-2"
      onClick={handleOpenBasicCellDialog}
    />
  );
};
